import axios from "axios";
import authUtil from "../utils/AuthUtils";
import notify from "../utils/notifier";
import { getServiceApiKey } from "../utils/apiKey";

const BASE_API_URL = `${window?.appConfig?.API_ENDPOINT}`;

// Helper function to create config for RAG API calls
const getRagConfig = async () => {
  const token = await authUtil.getDocumentServiceToken();
  return {
    headers: {
      "x-api-key": getServiceApiKey(),
      "x-document-service-integration-token": token,
      "Content-Type": "application/json",
    },
  };
};

// RAG API Service Functions

/**
 * Get RAG system health status
 */
async function getRagHealth() {
  try {
    const config = await getRagConfig();
    const response = await axios.get(`${BASE_API_URL}/rag/health`, config);
    return response.data;
  } catch (error) {
    console.error("RAG health check failed:", error);
    notify.error("Failed to check RAG system health");
    throw error;
  }
}

/**
 * Get RAG system status and document count
 */
async function getRagStatus() {
  try {
    const config = await getRagConfig();
    const response = await axios.get(`${BASE_API_URL}/rag/docs/status`, config);
    return response.data;
  } catch (error) {
    console.error("RAG status check failed:", error);
    notify.error("Failed to get RAG system status");
    throw error;
  }
}

/**
 * Chat with RAG system
 * @param {string} query - User's question
 * @param {string} sessionId - Optional session ID for conversation memory
 */
async function chatWithRag(query, sessionId = "default") {
  try {
    const config = await getRagConfig();
    const requestBody = {
      query: query,
      session_id: sessionId
    };
    
    console.log("Sending RAG request:", requestBody);
    const response = await axios.post(`${BASE_API_URL}/rag/chat`, requestBody, config);
    console.log("RAG API response:", response);
    
    // Check if the response indicates success or error
    if (response.data && response.data.success === false) {
      throw new Error(response.data.message || "RAG API returned error");
    }
    
    return response.data;
  } catch (error) {
    console.error("RAG chat failed:", error);
    console.error("Full error details:", error.response);
    
    // Don't show notification here - let component handle it
    throw error;
  }
}

// Search functionality removed - endpoint not yet implemented in backend

/**
 * Add new document to existing RAG system (preserves existing documents)
 * @param {string} content - Document content to add
 * @param {string} filename - Optional filename for the document
 */
async function addDocument(content, filename = null) {
  try {
    const token = await authUtil.getDocumentServiceToken();
    
    // Use FormData for all requests to match backend expectations
    const formData = new FormData();
    formData.append('content', content);
    if (filename) {
      formData.append('filename', filename);
    }
    
    const config = {
      headers: {
        "x-api-key": getServiceApiKey(),
        "x-document-service-integration-token": token,
        // Don't set Content-Type for FormData - let browser set boundary
      },
    };
    
    const response = await axios.post(`${BASE_API_URL}/rag/docs/add`, formData, config);
    return response.data;
  } catch (error) {
    console.error("RAG document add failed:", error);
    notify.error("Failed to add document to RAG system");
    throw error;
  }
}

/**
 * Add document file to existing RAG system
 * @param {File} file - File object from input
 * @param {string} filename - Optional custom filename
 */
async function addDocumentFile(file, filename = null) {
  try {
    const token = await authUtil.getDocumentServiceToken();
    
    const formData = new FormData();
    formData.append('file', file, filename || file.name);
    
    const config = {
      headers: {
        "x-api-key": getServiceApiKey(),
        "x-document-service-integration-token": token,
        // Don't set Content-Type for FormData - let browser set boundary
      },
    };
    
    const response = await axios.post(`${BASE_API_URL}/rag/docs/add`, formData, config);
    return response.data;
  } catch (error) {
    console.error("RAG file upload failed:", error);
    notify.error("Failed to upload file to RAG system");
    throw error;
  }
}

/**
 * Recover RAG system from PVC training documents
 */
async function recoverFromPvc() {
  try {
    const config = await getRagConfig();
    const response = await axios.post(`${BASE_API_URL}/rag/docs/recover`, {}, config);
    return response.data;
  } catch (error) {
    console.error("RAG recovery failed:", error);
    notify.error("Failed to recover RAG system");
    throw error;
  }
}

const RagService = {
  getRagHealth,
  getRagStatus,
  chatWithRag,
  addDocument,
  addDocumentFile,
  recoverFromPvc
};

export default RagService;